import { api } from "./client";

export interface ReportJob {
  id?: string;
  _id: string;
  user_id: string;
  role: "client" | "admin" | "analyst";
  domain: string;
  company_name: string;
  sector: string;
  sector_confirmed: boolean;
  period: "30D" | "90D" | "LAST_QUARTER" | string;
  format: "pdf";
  status: "queued" | "running" | "done" | "completed" | "failed" | "cancelled";
  step: string;
  progress: number;
  ai_status: "ok" | "fallback" | "unavailable" | string;
  file_id: string | null;
  file_size: number;
  pdf_path?: string;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  expires_at: string;
  expires_in_days?: number;
}

export interface ExistingReport {
  id: string;
  domain: string;
  sector: string;
  period: string;
  created_at: string;
  file_size: number;
}

export interface ReportJobsListResponse {
  jobs: ReportJob[];
  remaining_reports_today: number;
  daily_limit: number;
}

export async function createReportJob(
  domain: string,
  sector?: string,
  period = "30D",
  format = "pdf",
  idempotencyKey?: string,
  force: boolean = false
): Promise<ReportJob> {
  const key = idempotencyKey || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `key-${Date.now()}-${Math.random()}`);
  const res = await api.post(
    "/report-center/jobs",
    {
      domain,
      sector,
      period,
      format,
      idempotency_key: key,
      force
    },
    {
      headers: { "Idempotency-Key": key }
    }
  );
  return res.data;
}

export async function fetchReportJobs(scope = "mine"): Promise<ReportJobsListResponse> {
  const res = await api.get("/report-center/jobs", { params: { scope } });
  return res.data;
}

export async function fetchDomainReports(domain: string): Promise<ExistingReport[]> {
  try {
    const res = await api.get("/report-center/jobs", { params: { domain } });
    const jobs: ReportJob[] = res.data.jobs || [];
    return jobs
      .filter(j => j.status === "completed" || j.status === "done")
      .map(j => ({
        id: j._id || j.id || "",
        domain: j.domain,
        sector: j.sector,
        period: j.period,
        created_at: j.created_at,
        file_size: j.file_size
      }));
  } catch {
    return [];
  }
}

export async function fetchActiveReportJob(): Promise<ReportJob | null> {
  const res = await api.get("/report-center/jobs/active");
  return res.data.active_job || null;
}

export async function fetchReportJobStatus(jobId: string): Promise<ReportJob> {
  const res = await api.get(`/report-center/jobs/${jobId}`);
  return res.data;
}

export async function cancelReportJob(jobId: string): Promise<void> {
  await api.post(`/report-center/jobs/${jobId}/cancel`);
}

export async function downloadReportPdf(jobId: string, filename?: string): Promise<Blob> {
  const res = await api.get(`/report-center/jobs/${jobId}/download`, {
    responseType: "blob"
  });
  if (filename) {
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
  return res.data;
}

export function previewReportHtml(jobId: string) {
  api.get(`/report-center/jobs/${jobId}/preview`, { responseType: 'text' })
    .then(res => {
      const win = window.open("", "_blank");
      if (win) {
        win.document.write(res.data);
        win.document.close();
      }
    })
    .catch(err => {
      console.error("Failed to preview HTML:", err);
    });
}

export async function deleteReportJob(jobId: string): Promise<void> {
  await api.delete(`/report-center/jobs/${jobId}`);
}

export async function generateWeeklyReport(clientName: string) {
  const response = await api.get("/reports/weekly/pdf", {
    params: { client_name: clientName },
    responseType: "blob",
  });
  return response.data;
}
